import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  Typography,
  Container,
  useMediaQuery,
  Box,
  Button,
  TextareaAutosize,
  TextField,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  OutlinedInput,
} from "@mui/material";
import "../../styles/inputBox.css";
import InfoIcon from "../../assets/images/JobIcon/info.svg";
import {
  LocalizationProvider,
  DateTimePicker,
  DatePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import BlockhiringIcon from "../../assets/images/JobIcon/Block-hiring.svg";
import { useParams } from "react-router-dom";
import { getRequest, postRequest, putRequest } from "../../api/apiServices";
import { createJob, getCompany, updateJob } from "../../api/apiList";
import { finalizeJobSpecVonqMapping } from "../../utils/contractTypeMapping";
import {
  buildJobProfileFifthSnapshot,
  isSelfEmittedFormUpdate,
} from "../../utils/jobSpecFormSync";
import DeleteIcon from "../../assets/images/deleteIcon.svg";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { StringChain } from "lodash";
import Calendy from "../Calendy";
import CalendlyUrlErrorPopup from "../Popups/CalendlyUrlErrorPopup";
import { useAuth } from "../../hooks/useAuth";
import { useDateTimePickerDefaultizedProps } from "@mui/x-date-pickers/DateTimePicker/shared";
import Toast from "../../utils/Toast";
import Loader from "../Loader";

interface JobProfilefifthProps {
  onBack: () => void;
  onNext: () => void; // Function to navigate to the next component
  setFormData: (data: any) => void;
  initialData: any;
  mode: any;
}

interface Step {
  stepNumber: number;
  content: string;
  customContent?: string;
}

function dayjsToIsoOrNull(date: Dayjs | null): string | null {
  if (!date || !date.isValid()) return null;
  return date.toISOString();
}

function isIncompleteDateEntry(date: Dayjs | null): boolean {
  return date !== null && !date.isValid();
}

const JobProfilefifth: React.FC<JobProfilefifthProps> = ({
  onBack,
  onNext,
  setFormData,
  initialData,
  mode,
}: JobProfilefifthProps) => {
  const isMobile = useMediaQuery("(max-width:578px)");
  const isTabs = useMediaQuery("(max-width:1200px)");
  const { user } = useAuth();
  const [positionTimeline, setPositionTimeline] = useState<Dayjs | null>(null);
  const [urgentNeeds, setUrgentNeeds] = useState("");
  const [additionalRequirements, setAdditionalRequirements] = useState("");
  const [openCalendly, setOpenCanlendly] = useState(false);
  const [openCalendlyUrlError, setOpenCalendlyUrlError] = useState(false);
  const [calendlyMeetingLink, setCalendlyMeetingLink] = useState("");
  const [loading, setLoading] = useState(false);

  // Add Step
  const [interviewProcess, setInterviewProcess] = useState<Step[]>([
    { stepNumber: 1, content: "" },
  ]);

  const handleAddStep = (): void => {
    const maxStepNumber = interviewProcess.length > 0 
      ? Math.max(...interviewProcess.map(s => s.stepNumber || 0))
      : 0;
    const newStep: Step = {
      stepNumber: maxStepNumber + 1,
      content: "",
    };
    setInterviewProcess([...interviewProcess, newStep]);
  };

  const handleDropdownChange = (index: number, content: string): void => {
    const updatedSteps = [...interviewProcess];
    updatedSteps[index].content = content;
    if (content !== "Other") {
      updatedSteps[index].customContent = "";
    }
    setInterviewProcess(updatedSteps);

    if (content.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        interviewProcess: "",
      }));
    }
  };

  const handleDeleteStep = (index: number): void => {
    const updatedSteps = interviewProcess.filter((_, i) => i !== index);
    // Reorder step numbers to be sequential
    updatedSteps.forEach((step, i) => {
      step.stepNumber = i + 1;
    });
    setInterviewProcess(updatedSteps);
  };

  const handleCustomContentChange = (
    index: number,
    customContent: string
  ): void => {
    const updatedSteps = [...interviewProcess];
    updatedSteps[index].customContent = customContent;
    setInterviewProcess(updatedSteps);

    if (customContent.trim() !== "") {
      setErrors((prevErrors) => ({
        ...prevErrors,
        interviewProcess: "",
      }));
    }
  };

  const params = useParams();

  const [errors, setErrors] = useState({
    positionTimeline: "",
    urgentNeeds: "",
    interviewProcess: "",
    additionalRequirements: "",
  });

  // Sync local state with initialData whenever it changes (for both new and existing jobs)
  // Use refs to track state and prevent infinite loops
  const isUpdatingFromInitialData = useRef(false);
  const lastInitialDataRef = useRef<string>("");
  const lastLocalStateRef = useRef<string>("");
  const lastEmittedSnapshotRef = useRef<string | null>(null);
  const hasHydratedRef = useRef(false);
  const loadedJobIdRef = useRef<string | number | null>(null);
  const positionTimelineRef = useRef<Dayjs | null>(null);

  useEffect(() => {
    positionTimelineRef.current = positionTimeline;
  }, [positionTimeline]);

  useEffect(() => {
    if (initialData && Object.keys(initialData).length > 0) {
      const incomingSnapshot = buildJobProfileFifthSnapshot(initialData);
      if (isSelfEmittedFormUpdate(incomingSnapshot, lastEmittedSnapshotRef)) {
        hasHydratedRef.current = true;
        lastInitialDataRef.current = incomingSnapshot;
        return;
      }

      const jobId = initialData.id as string | number | undefined;
      const isNewExternalJobLoad =
        jobId != null && loadedJobIdRef.current !== jobId;
      const shouldHydrate = isNewExternalJobLoad || !hasHydratedRef.current;

      if (!shouldHydrate) {
        return;
      }

      const initialDataStr = incomingSnapshot;

      if (lastInitialDataRef.current === initialDataStr) {
        return;
      }
      
      lastInitialDataRef.current = initialDataStr;

      if (jobId != null) {
        loadedJobIdRef.current = jobId;
      }
      
      isUpdatingFromInitialData.current = true;
      
      // Handle positionTimeline
      if (initialData.positionTimeline) {
        const timelineDate = dayjs(initialData.positionTimeline);
        setPositionTimeline(timelineDate.isValid() ? timelineDate : null);
      } else if (initialData.hasOwnProperty('positionTimeline') && !initialData.positionTimeline) {
        const localTimeline = positionTimelineRef.current;
        if (localTimeline === null || localTimeline.isValid()) {
          setPositionTimeline(null);
        }
      }
      
      // Handle urgentNeeds
      if (initialData.hasOwnProperty('urgentNeeds')) {
        setUrgentNeeds(initialData.urgentNeeds || "");
      }
      
      // Handle interviewProcess - ensure proper array structure and sync
      if ('interviewProcess' in initialData) {
        if (Array.isArray(initialData.interviewProcess) && initialData.interviewProcess.length > 0) {
          // Ensure each step has proper structure and normalize content values
          const currentProcess = initialData.interviewProcess.map((step: any, idx: number) => {
            const content = step.content ? String(step.content).trim() : "";
            return {
              stepNumber: step.stepNumber || idx + 1,
              content: content,
              customContent: step.customContent ? String(step.customContent).trim() : ""
            };
          });
          setInterviewProcess(currentProcess);
        } else if (Array.isArray(initialData.interviewProcess) && initialData.interviewProcess.length === 0) {
          // Empty array - reset to default
          setInterviewProcess([{ stepNumber: 1, content: "" }]);
        }
      }
      
      // Handle additionalRequirements
      if (initialData.hasOwnProperty('additionalRequirements')) {
        setAdditionalRequirements(initialData.additionalRequirements || "");
      }
      
      // Update the last local state ref to match what we just set
      lastLocalStateRef.current = initialDataStr;
      hasHydratedRef.current = true;
      
      setTimeout(() => {
        isUpdatingFromInitialData.current = false;
      }, 0);
    }
  }, [initialData]);

  // Update formData whenever any field changes to ensure all data is available when navigating to other steps
  useEffect(() => {
    if (isUpdatingFromInitialData.current) {
      return;
    }

    if (!hasHydratedRef.current) {
      return;
    }
    
    if (!initialData || Object.keys(initialData).length === 0) {
      return;
    }
    
    // Create current state snapshot for comparison
    const typingDate = isIncompleteDateEntry(positionTimeline);
    const syncedTimeline = dayjsToIsoOrNull(positionTimeline);
    const timelineForParent = typingDate
      ? (initialData.positionTimeline ?? null)
      : syncedTimeline;

    const currentStateStr = JSON.stringify({
      positionTimeline: timelineForParent,
      urgentNeeds: urgentNeeds,
      interviewProcess: interviewProcess,
      additionalRequirements: additionalRequirements,
    });
    
    // Only update if state has actually changed from last known state
    if (lastLocalStateRef.current === currentStateStr) {
      return;
    }
    
    lastLocalStateRef.current = currentStateStr;
    
    const currentData = {
      ...initialData,
      urgentNeeds,
      interviewProcess,
      additionalRequirements,
    };
    if (!typingDate) {
      currentData.positionTimeline = syncedTimeline;
    }

    lastEmittedSnapshotRef.current = buildJobProfileFifthSnapshot(currentData);
    setFormData(currentData);
  }, [positionTimeline, urgentNeeds, interviewProcess, additionalRequirements, initialData, setFormData]);

  const formIsValid = () => {
    const stepErrors = interviewProcess?.map((step) => {
      if (
        !step.content ||
        (step.content === "Other" && !step.customContent?.trim())
      ) {
        return "Step content is required";
      }
      return ""; // No error
    });

    const newErrors = {
      positionTimeline:
        !positionTimeline?.isValid() ? "Timeline is required" : "",
      urgentNeeds: !urgentNeeds?.trim() ? "Urgent Hiring is required" : "",
      interviewProcess: stepErrors.some((error) => error !== "")
        ? "Please fill out all interview steps correctly"
        : "",
      additionalRequirements: "",
    };
    setErrors(newErrors);
    return Object.values(newErrors).every((error) => error === "");
  };

  const handleBack = (event: any) => {
    event.preventDefault();
    onBack(); // Call the onNext function to navigate to the next component
  };

  const handleSubmit = async (event: any) => {
    event.preventDefault();
    const data = {
      positionTimeline: dayjsToIsoOrNull(positionTimeline) || "",
      urgentNeeds,
      interviewProcess: interviewProcess,
      additionalRequirements,
    };

    const merged = { ...initialData, ...data };
    const allData = {
      ...merged,
      vonqMapping: finalizeJobSpecVonqMapping(merged),
    };

    if (formIsValid()) {
      setLoading(true);

      try {
        let response;

        if (mode) {
          response = await putRequest(updateJob(params?.id), allData);
          if (response) {
            setLoading(false);
            setFormData(response?.data?.data);
            Toast(response?.data?.statusMessage || "Jobs Updated Successfully");
            onNext();
          }
        } else {
          response = await postRequest(createJob, allData);
          if (response) {
            setLoading(false);
            setFormData(response?.data?.data);
            Toast(response?.data?.statusMessage || "Jobs Created Successfully");
            onNext();
          }
        }
        setLoading(false);
      } catch (error) {
        setLoading(false);
        console.error("Error during form submission:", error);
      }
    }
    // if (formIsValid()) {
    //   if (mode) {
    //     setLoading(true);
    //     const response = await putRequest(updateJob(params?.id), data);
    //     setFormData(response?.data?.data);
    //     setLoading(false);
    //     onNext();
    //   } else {
    //     const allData = {
    //       ...initialData,
    //       ...data,
    //     };
    //     setFormData(allData);
    //     onNext();
    //   }
    // }
  };

  const disablePastDates = (date: Dayjs) => {
    return date.isBefore(dayjs(), "day");
  };

  useEffect(() => {
    const fetchCompanyCalendlyLink = async () => {
      if (!user?.uId) return;
      try {
        const response = await getRequest(getCompany(String(user.uId)));
        setCalendlyMeetingLink(
          response?.data?.data?.calendlyMeetingLink || ""
        );
      } catch (error) {
        console.error("Error fetching company Calendly link:", error);
      }
    };

    fetchCompanyCalendlyLink();
  }, [user?.uId]);

  const isValidCalendlyUrl = (url: string | undefined): boolean => {
    if (!url || url.trim() === "") {
      return false;
    }
    const calendlyPattern = /^https:\/\/(www\.)?calendly\.com\/.+/i;
    return calendlyPattern.test(url.trim());
  };

  const handleCalendyOpen = (event: React.MouseEvent) => {
    event.stopPropagation();
    if (!isValidCalendlyUrl(calendlyMeetingLink)) {
      setOpenCalendlyUrlError(true);
      return;
    }
    setOpenCanlendly(true);
  };

  const handleCalendyEvent = (eventData: {
    name: string;
    email: string;
    preparationDetails: string;
  }) => {};

  return (
    <>
      <div>
        <Container
          style={{
            display: "flex",
            padding: "25px",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Grid
            spacing={2}
            bgcolor="#FFFFFF"
            width="884px"
            borderRadius="22px"
            border="1px solid #D6D6D7"
            margin={0}
          >
            <Grid
              item
              height={isMobile ? "auto" : "auto"}
              style={{ padding: 30 }}
            >
              <Grid
                container
                direction="row"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                gap="10px"
              >
                <Grid
                  item
                  width="fit-content"
                  display="flex"
                  alignItems="center"
                  gap="10px"
                >
                  <ArrowBackIcon
                    onClick={(event) => handleBack(event)}
                    style={{ cursor: "pointer" }}
                  />
                  <Typography
                    color="#273848"
                    fontWeight={700}
                    fontSize="20.94px"
                  >
                    Interview Process
                  </Typography>
                </Grid>
                <Grid item gap={2} display="flex">
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#84E1CC",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#84E1CC",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#84E1CC",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#84E1CC",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#84E1CC",
                    }}
                  />
                  <Box
                    sx={{
                      width: 36,
                      height: 8,
                      borderRadius: 4,
                      bgcolor: "#DFF4EF",
                    }}
                  />
                </Grid>
              </Grid>
              <Grid mt="16px"></Grid>

              {/* <Grid
              display="flex"
              alignItems="center"
              border="1px solid #c3c3c3"
              borderRadius="8px"
              padding="8px 0px 6px 15px"
            >
              <Grid direction="row" display="flex" gap={1} alignItems="center">
                <img src={InfoIcon} alt="team-icon" />
                <Typography
                  //   className="input-label-custom"
                  fontWeight={700}
                  fontSize="13.94px"
                  lineHeight="13.94px"
                  color="#273848"
                >
                  Interview process & Additional information
                </Typography>
              </Grid>
            </Grid> */}

              <Typography
                color="#273848"
                fontWeight={700}
                lineHeight="14.94px"
                fontSize="15px"
                //   className="input-label-custom"
              >
                Timeline
              </Typography>
              <Grid mt="19px"></Grid>
              <Grid item>
                <Typography className="input-label-custom">
                  What is the expected timeline for filling this position?{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DatePicker
                    label="Select Date"
                    format="DD/MM/YYYY"
                    value={positionTimeline}
                    onChange={(date: Dayjs | null) => {
                      setPositionTimeline(date);
                      if (date?.isValid()) {
                        setErrors((prevErrors) => ({
                          ...prevErrors,
                          positionTimeline: "",
                        }));
                      }
                    }}
                    shouldDisableDate={disablePastDates}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                        error: !!errors.positionTimeline,
                        placeholder: "DD/MM/YYYY",
                      },
                      field: {
                        clearable: true,
                      },
                    }}
                  />
                </LocalizationProvider>
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.positionTimeline}
                </InputLabel>
              </Grid>
              <Grid mt="19px"></Grid>
              <Grid item>
                <Typography className="input-label-custom">
                  What is the impact of not meeting the expected hiring
                  deadline? <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={1}></Grid>
                <TextField
                  sx={{
                    // mt: 1,
                    "& .css-8ewcdo-MuiInputBase-root-MuiOutlinedInput-root": {
                      padding: "12px 11px", 
                    },
                  }}
                  placeholder="What is the impact of not meeting the expected hiring deadline?"
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={10}
                  className="input-box-custom"
                  error={!!errors.urgentNeeds}
                  helperText={errors.urgentNeeds}
                  value={urgentNeeds}
                  onChange={(e) => {
                    const value = e.target.value;
                    setUrgentNeeds(value);
                    if (value.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        urgentNeeds: "",
                      }));
                    }
                  }}
                />
              </Grid>
              <Grid mt="19px"></Grid>
              <Typography
                color="#273848"
                fontWeight={700}
                lineHeight="14.94px"
                fontSize="15px"
                //   className="input-label-custom"
              >
                Interview Process
              </Typography>
              <Grid mt="19px"></Grid>
              <Grid item>
                <Typography className="input-label-custom">
                  Can you outline the interview process, including the number of
                  rounds and key decision-makers?{" "}
                  <span style={{ color: "red" }}>*</span>
                </Typography>
                <Grid mb={3}></Grid>
                <Button
                  type="submit"
                  variant="contained"
                  style={{
                    backgroundColor: "#84E1CC",
                    boxShadow: "none",
                    borderRadius: "5px",
                    padding: "17px 17px",
                    lineHeight: "0px",
                    letterSpacing: "0px",
                    textTransform: "none",
                    marginBottom: "5px",
                  }}
                  onClick={handleAddStep}
                >
                  Add Step
                </Button>
                {interviewProcess?.map((step, index) => (
                  <Box key={`step-${step.stepNumber}-${index}`} sx={{ mb: 2 }}>
                    <Grid
                      container
                      direction="row"
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      gap={isMobile ? "10px" : ""}
                    >
                      <Typography>Step {step.stepNumber}</Typography>
                    </Grid>
                    <Select
                      value={step.content ? String(step.content).trim() : ""}
                      onChange={(e) =>
                        handleDropdownChange(index, e.target.value as string)
                      }
                      displayEmpty
                      size="small"
                      sx={{
                        mb: step.content === "Other" ? 2 : 0,
                        width: "92%",
                      }}
                      renderValue={(selected) => {
                        const value = selected ? String(selected).trim() : "";
                        if (!value || value === "") {
                          return <span style={{ color: "#999" }}>Select Step Content</span>;
                        }
                        // Display the value even if it doesn't match a MenuItem (for custom values)
                        return <span>{value}</span>;
                      }}
                    >
                      <MenuItem value="" disabled>
                        Select Step Content
                      </MenuItem>
                      <MenuItem value="First Interview">
                        First Interview
                      </MenuItem>
                      <MenuItem value="Second Interview">
                        Second Interview
                      </MenuItem>
                      <MenuItem value="Third Interview">
                        Third Interview
                      </MenuItem>
                      <MenuItem value="Final Interview">
                        Final Interview
                      </MenuItem>
                      <MenuItem value="Assessment">Assessment</MenuItem>
                      <MenuItem value="Trial Day">Trial Day</MenuItem>
                      <MenuItem value="Employment Conditions">
                        Employment Conditions
                      </MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                    <IconButton
                      sx={{ color: "red", ml: 2 }}
                      onClick={() => handleDeleteStep(index)}
                    >
                      <img
                        src={DeleteIcon}
                        alt="Delete"
                        style={{ width: "20px", height: "26px", color: "red" }}
                      />
                    </IconButton>

                    {step.content === "Other" && (
                      <TextField
                        sx={{
                          // mt: 1,
                          "& .MuiOutlinedInput-input": {
                            padding: "10px 11px", // Add padding here
                          },
                        }}
                        variant="outlined"
                        placeholder="Enter custom step content"
                        fullWidth
                        onChange={(e) =>
                          handleCustomContentChange(index, e.target.value)
                        }
                        size="small"
                        value={step.customContent || ""} // Bind the value to customContent
                      />
                    )}
                  </Box>
                ))}
                <InputLabel
                  style={{
                    color: "#f44336",
                    paddingTop: "3px",
                    fontSize: "12px",
                    paddingLeft: "10px",
                  }}
                >
                  {errors.interviewProcess}
                </InputLabel>
              </Grid>
              <Grid mt="10px"></Grid>
              <Grid
                item
                bgcolor="#84E1CC"
                padding="9px 32px 9px 16px"
                width="fit-Content"
                borderRadius="4px"
                onClick={handleCalendyOpen}
                sx={{ cursor: "pointer" }}
              >
                <Grid
                  direction="row"
                  display="flex"
                  alignItems="center"
                  gap="2.5px"
                >
                  <img src={BlockhiringIcon} alt="instagram-icon" />
                  <Typography
                    fontWeight={500}
                    fontSize="12.55px"
                    color="#FFFFFF"
                  >
                    Block time slots in agenda hiring manager
                  </Typography>
                </Grid>
              </Grid>
              <Grid mb="17px"></Grid>
              <Typography
                color="#273848"
                fontWeight={700}
                lineHeight="14.94px"
                fontSize="15px"
                //   className="input-label-custom"
              >
                Any Additional Information
              </Typography>
              <Grid mt="19px"></Grid>
              <Grid item>
                <Typography className="input-label-custom">
                  Is there any other information or specific requirements that
                  should be considered?
                </Typography>
                <Grid mb={1}></Grid>
                <OutlinedInput
                  placeholder="Type here..."
                  multiline
                  minRows={4}
                  maxRows={10}
                  className="text-area-size"
                  value={additionalRequirements}
                  onChange={(e) => {
                    const value = e.target.value;
                    setAdditionalRequirements(value);
                    if (value.trim() !== "") {
                      setErrors((prevErrors) => ({
                        ...prevErrors,
                        additionalRequirements: "",
                      }));
                    }
                  }}
                />
              </Grid>
              <InputLabel
                style={{
                  color: "#f44336",
                  paddingTop: "3px",
                  fontSize: "12px",
                  paddingLeft: "10px",
                }}
              >
                {errors.additionalRequirements}
              </InputLabel>
              <Grid mt="30px"></Grid>
              <Grid
                container
                direction="row"
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Grid xs={isTabs ? 0 : 2}>
                  <button
                    type="submit"
                    className="user-scale"
                    // variant="contained"
                    // fullWidth
                    style={{
                      backgroundColor: "#FFFFFF",
                      border: "1px solid #000000",
                      color: "#273848",
                      boxShadow: "none",
                      borderRadius: "5px",
                      padding: "17px 17px",
                      lineHeight: "0px",
                      letterSpacing: "0px",
                      textTransform: "none",
                      width: "100%",
                      // padding: "10px 50px",
                    }}
                    onClick={(event) => handleBack(event)}
                  >
                    Back
                  </button>
                </Grid>
                <Grid
                  item
                  xs={isTabs ? 0 : mode ? 3 : 2}
                  style={{ maxWidth: "fitContent" }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    style={{
                      backgroundColor: "#84E1CC",
                      boxShadow: "none",
                      borderRadius: "5px",
                      padding: loading ? "7px" : "17px 17px",
                      lineHeight: "0px",
                      textTransform: "none",
                      letterSpacing: "0px",
                    }}
                    onClick={(event) => handleSubmit(event)}
                  >
                    {mode ? loading ? <Loader /> : "Save & Next" : "Continue"}
                  </Button>
                </Grid>
              </Grid>
              <Grid mb="10px"></Grid>
            </Grid>
          </Grid>
        </Container>
      </div>
      {openCalendly && (
        <Calendy
          meetingLink={calendlyMeetingLink}
          onEventScheduled={handleCalendyEvent}
          userId={Number(user?.uId)}
          onScheduleBehavior="close"
          onClose={() => setOpenCanlendly(false)}
        />
      )}
      {openCalendlyUrlError && (
        <CalendlyUrlErrorPopup
          open={openCalendlyUrlError}
          onClose={() => setOpenCalendlyUrlError(false)}
        />
      )}
    </>
  );
};

export default JobProfilefifth;
